import {
	QueryClient,
	useInfiniteQuery,
	useMutation,
	useQuery,
} from '@tanstack/react-query';
import axios from 'axios';
import { isEmpty } from 'lodash';
import { UseFormReset } from 'react-hook-form';
import {
	DISBURSEMENT_API_MUTATION__CREATE_ERROR_TOAST,
	DISBURSEMENT_API_MUTATION__CREATE_SUCCESS_TOAST,
	DISBURSEMENT_API_MUTATION__DISCARD_SUCCESS_TOAST,
	DISBURSEMENT_API_MUTATION__ISSUE_SUCCESS_TOAST,
	DISBURSEMENT_API_MUTATION__UPDATE_ERROR_TOAST,
	DISBURSEMENT_API_MUTATION__UPDATE_SUCCESS_TOAST,
} from '../components/disbursements/data/constants';
import { DisbursementFormValues } from '../components/fastForm/formBuilders/getDisbursementForm';
import { errorToast, successToast } from '../components/Toast';
import {
	Disbursement,
	DisbursementActionRequest,
	DisbursementCreateRequest,
	DisbursementRequest,
	DisbursementResponse,
	DisbursementState,
} from '../types/disbursements';
import CashBalanceApi from './CashBalanceApi';

import { getApiUrl } from '../util/util';
import { get, patch, post } from './http';

const DisbursementsApi = {
	getDisbursements(
		authorizationToken: string,
		pagingToken?: string,
		pageSize?: number,
		policyholderLocator?: string,
		policyLocator?: string,
		state?: string,
	) {
		const params = new URLSearchParams();
		if (pagingToken) {
			params.append('pagingToken', pagingToken);
		}
		if (pageSize && !pagingToken) {
			params.append('pageSize', pageSize.toString());
		}

		if (policyholderLocator && !pagingToken) {
			params.append('policyholderLocator', policyholderLocator);
		}

		if (policyLocator && !pagingToken) {
			params.append('policyLocator', policyLocator);
		}

		if (state && !pagingToken) {
			params.append('state', state);
		}

		const url = `${getApiUrl()}/disbursements?${params.toString()}`;
		return get<DisbursementResponse>(url, authorizationToken);
	},
	getDisbursement(authorizationToken: string, disbursementLocator: string) {
		return get<Disbursement>(
			`${getApiUrl()}/disbursements/${disbursementLocator}`,
			authorizationToken,
		);
	},
	createDisbursement(
		authorizationToken: string,
		cashBalanceLocator: string,
		disbursement: DisbursementCreateRequest,
	) {
		return post<Disbursement>(
			`${getApiUrl()}/cashBalances/${cashBalanceLocator}/disbursements`,
			authorizationToken,
			disbursement,
		);
	},
	updateDisbursement(
		authorizationToken: string,
		disbursementLocator: string,
		disbursement: DisbursementActionRequest,
	) {
		return patch<Disbursement>(
			`${getApiUrl()}/disbursements/${disbursementLocator}`,
			authorizationToken,
			disbursement,
		);
	},
};

export const getDisbursementsQueryKey = (
	authToken: string,
	policyholderLocator: string,
	policyLocator: string,
) => ['disbursements', authToken, policyholderLocator, policyLocator];

export const useDisbursements = (
	authToken: string,
	cashManagementEnabled = false,
	hasPermission: boolean,
	policyholderLocator?: string,
	policyLocator?: string,
) =>
	useQuery({
		queryKey: getDisbursementsQueryKey(
			authToken,
			policyholderLocator,
			policyLocator,
		),
		queryFn: async () => {
			const response = await DisbursementsApi.getDisbursements(
				authToken,
				null,
				null,
				policyholderLocator,
				policyLocator,
			);
			return response.data.disbursements;
		},
		enabled: cashManagementEnabled && hasPermission,
	});

export const useCreateDisbursementMutation = ({
	authToken,
	queryClient,
}: {
	authToken: string;
	queryClient: QueryClient;
}) =>
	useMutation({
		mutationFn: (disbursement: DisbursementRequest) =>
			DisbursementsApi.createDisbursement(
				authToken,
				disbursement.locator, // cash balance locator
				disbursement.request as DisbursementCreateRequest,
			),
		onSuccess: async (newDisbursement, createRequest) => {
			queryClient.invalidateQueries(
				getDisbursementQueryKey(authToken, newDisbursement.data.locator),
			);
			successToast(DISBURSEMENT_API_MUTATION__CREATE_SUCCESS_TOAST);
		},
		onError: (error) => {
			if (axios.isAxiosError(error)) {
				errorToast(
					DISBURSEMENT_API_MUTATION__CREATE_ERROR_TOAST +
						error.response?.data.message,
				);
			}
		},
	});

export const useUpdateDisbursementAfterCreationMutation = ({
	authToken,
	queryClient,
}: {
	authToken: string;
	queryClient: QueryClient;
}) =>
	useMutation({
		mutationFn: (disbursement: DisbursementRequest) =>
			DisbursementsApi.updateDisbursement(
				authToken,
				disbursement.locator,
				disbursement.request as DisbursementActionRequest,
			),
		onSuccess: (newDisbursement, createRequest) => {
			queryClient.invalidateQueries([
				'disbursements',
				createRequest.locator,
				authToken,
			]);
			successToast(DISBURSEMENT_API_MUTATION__CREATE_SUCCESS_TOAST);
		},
		onError: (error) => {
			if (axios.isAxiosError(error)) {
				errorToast(
					DISBURSEMENT_API_MUTATION__UPDATE_ERROR_TOAST +
						error.response?.data.message,
				);
			}
		},
	});

export const useUpdateDisbursementMutation = ({
	disbursementLocator,
	authToken,
	reset,
	formValues,
	queryClient,
}: {
	disbursementLocator: string;
	authToken: string;
	reset: UseFormReset<DisbursementFormValues>;
	formValues: DisbursementFormValues;
	queryClient: QueryClient;
}) =>
	useMutation({
		onMutate: async (updatedDisbursement) => {
			// Do an optimistic update to the cache so navigating back to the disbursement page is snappy and the values reflect the new state
			await queryClient.cancelQueries({
				queryKey: ['disbursements', disbursementLocator, authToken],
			});

			const previousDisbursement = queryClient.getQueryData<Disbursement>([
				'disbursements',
				disbursementLocator,
				authToken,
			]);

			return { previousDisbursement, updatedDisbursement };
		},
		mutationFn: (disbursement: DisbursementActionRequest) =>
			DisbursementsApi.updateDisbursement(
				authToken,
				disbursementLocator,
				disbursement,
			),
		onSuccess: (response, request) => {
			queryClient.invalidateQueries([
				'disbursements',
				disbursementLocator,
				authToken,
			]);
			reset(formValues);
			const successMsg =
				request?.action === 'discard'
					? DISBURSEMENT_API_MUTATION__DISCARD_SUCCESS_TOAST
					: DISBURSEMENT_API_MUTATION__UPDATE_SUCCESS_TOAST;
			successToast(successMsg);
		},
		onError: (error) => {
			if (axios.isAxiosError(error)) {
				errorToast(
					DISBURSEMENT_API_MUTATION__UPDATE_ERROR_TOAST +
						error.response?.data.message,
				);
			}
		},
	});

export const useIssueDisbursementMutation = ({
	disbursementLocator,
	authToken,
	reset,
	formValues,
	queryClient,
}: {
	disbursementLocator: string;
	authToken: string;
	reset: UseFormReset<DisbursementFormValues>;
	formValues: DisbursementFormValues;
	queryClient: QueryClient;
}) =>
	useMutation({
		onMutate: async (updatedDisbursement) => {
			// Do an optimistic update to the cache so navigating back to the disbursement page is snappy and the values reflect the new state
			await queryClient.cancelQueries({
				queryKey: ['disbursements', disbursementLocator, authToken],
			});

			const previousDisbursement = queryClient.getQueryData<Disbursement>([
				'disbursements',
				disbursementLocator,
				authToken,
			]);

			return { previousDisbursement, updatedDisbursement };
		},
		mutationFn: async (disbursement: DisbursementActionRequest) => {
			if (!isEmpty(disbursement.disbursementUpdate)) {
				// save before issuing
				await DisbursementsApi.updateDisbursement(
					authToken,
					disbursementLocator,
					{ disbursementUpdate: disbursement.disbursementUpdate },
				);
				await DisbursementsApi.updateDisbursement(
					authToken,
					disbursementLocator,
					{ action: disbursement.action },
				);
			} else {
				await DisbursementsApi.updateDisbursement(
					authToken,
					disbursementLocator,
					{ action: disbursement.action },
				);
			}
		},
		onSuccess: (response, request) => {
			queryClient.invalidateQueries([
				'disbursements',
				disbursementLocator,
				authToken,
			]);
			reset(formValues);
			successToast(DISBURSEMENT_API_MUTATION__ISSUE_SUCCESS_TOAST);
		},
		onError: (error) => {
			if (axios.isAxiosError(error)) {
				errorToast(
					DISBURSEMENT_API_MUTATION__UPDATE_ERROR_TOAST +
						error.response?.data.message,
				);
			}
		},
	});

export const getPaginatedDisbursementsQueryKey = (
	pageSize: number,
	token: string,
	policyholderLocator?: string,
	policyLocator?: string,
	state?: DisbursementState,
) => [
	'disbursements',
	pageSize,
	token,
	policyholderLocator,
	policyLocator,
	state,
];

export const usePaginatedDisbursements = (
	pageSize: number,
	token: string,
	policyholderLocator?: string,
	policyLocator?: string,
	state?: DisbursementState,
) => {
	return useInfiniteQuery(
		getPaginatedDisbursementsQueryKey(
			pageSize,
			token,
			policyholderLocator,
			policyLocator,
			state,
		),
		async ({ pageParam = 0 }) => {
			const response = await DisbursementsApi.getDisbursements(
				token,
				pageParam,
				pageSize,
				policyholderLocator,
				policyLocator,
				state,
			);
			return response;
		},
		{
			getNextPageParam: (lastPage) =>
				lastPage.data.pagingToken === ''
					? undefined
					: lastPage.data.pagingToken,
			keepPreviousData: true,
		},
	);
};

export const getDisbursementQueryKey = (
	disbursementLocator: string,
	authToken: string,
) => ['disbursements', disbursementLocator, authToken];

export const useDisbursement = (
	disbursementLocator: string,
	authToken: string,
	cashManagementEnabled = false,
	hasFetchPermission = false,
) => {
	return useQuery({
		queryKey: getDisbursementQueryKey(disbursementLocator, authToken),
		queryFn: async () => {
			const response = await DisbursementsApi.getDisbursement(
				authToken,
				disbursementLocator,
			);
			const disbursementResponse = {
				...response.data,
			};
			const cashBalanceLocator = response.data?.cashBalanceLocator;
			if (cashBalanceLocator) {
				const cashBalance = await CashBalanceApi.getCashBalance(
					authToken,
					cashBalanceLocator,
				);
				const policyholderLocator = cashBalance.data.policyholderLocator;
				const policyLocator = cashBalance.data.policyLocator;
				disbursementResponse.policyLocator = policyLocator;
				disbursementResponse.policyholderLocator = policyholderLocator;
			}

			return disbursementResponse;
		},
		enabled:
			!!disbursementLocator && cashManagementEnabled && hasFetchPermission,
	});
};

export default DisbursementsApi;
